/**
 * Chart calculation - sidereal positions, houses, angles.
 */

import * as swe from "./ephemeris";
import { pymod } from "../lib/pyutil";
import type { PlainDateTime } from "../lib/datetime";
import { addSeconds, formatISODate } from "../lib/datetime";
import type { AstrologyConfig } from "./config";
import {
  PLANETS,
  SIGN_NAMES,
  NAKSHATRA_NAMES,
  NAKSHATRA_SPAN_DEG,
  NAKSHATRA_PADA_DEG,
} from "./constants";
import type { PlanetPosition, RawChartData, ShadbalaLite, FunctionalRoles } from "./models";
import { createShadbalaLite } from "./models";
import {
  compute_combustion,
  compute_graha_yuddha,
  compute_moon_paksha,
  dignity_flag,
  positional_dignity_strength,
  compute_dig_bala,
  compute_cheshta_bala,
  compute_functional_roles,
} from "./helpers";

// Once ensure_swiss_ephemeris has succeeded it never needs to run again: the sidereal mode is
// fixed to Lahiri for the app's lifetime and ephemeris.ts has no teardown/de-init path. The
// guard keeps this per-body-per-chart call from crossing the WASM boundary every time.
let _swiss_ready = false;

/** Set the canonical Swiss Ephemeris path and Lahiri sidereal mode. */
export function ensure_swiss_ephemeris(): void {
  if (_swiss_ready) {
    return;
  }
  // Python verifies the swiss/ dir exists then calls set_ephe_path + set_sid_mode.
  // In the port the data files are loaded once by initEphemeris(); set_ephe_path()
  // is a shim that throws if initialisation hasn't happened — same failure mode.
  swe.set_ephe_path();
  swe.set_sid_mode(swe.SIDM_LAHIRI);
  _swiss_ready = true;
}

/** Normalize longitude to 0-360 degrees. */
export function norm_deg(x: number): number {
  return pymod(pymod(x, 360) + 360, 360);
}

/** Derive sign information from sidereal longitude. */
export function get_sign_from_longitude(longitude: number): [number, string, number] {
  const lon = norm_deg(longitude);
  const sign = Math.trunc(Math.floor(lon / 30));
  const degree_in_sign = pymod(lon, 30);
  return [sign, SIGN_NAMES[sign], degree_in_sign];
}

/** Get nakshatra number, name, and pada from sidereal longitude.
 *
 * INTENTIONAL CPython-parity quirk (do not "fix"): at exact boundary longitudes (e.g. 40.0)
 * the division-based index and the fmod-based position can disagree, yielding an incoherent
 * index/position pair — and legacy_engine.moon_nakshatra_pada (pyfloordiv-based, 1-based)
 * disagrees there too. This mirrors the Python source exactly and is fixture-locked; the same
 * quirk lives in helpers.get_nakshatra and dasha.init_dasha_jd. See pyfloat.ts. */
export function get_nakshatra(longitude: number): [number, string, number] {
  const lon = norm_deg(longitude);
  const nak_num = pymod(Math.trunc(lon / NAKSHATRA_SPAN_DEG), 27);
  const pos_in_nak = pymod(lon, NAKSHATRA_SPAN_DEG);
  const pada = Math.min(Math.trunc(pos_in_nak / NAKSHATRA_PADA_DEG) + 1, 4);
  return [nak_num, NAKSHATRA_NAMES[nak_num], pada];
}

/** Calculate house number (1-12) from planet sign and ascendant sign. */
export function get_house_from_signs(planet_sign: number, asc_sign: number): number {
  return pymod(planet_sign - asc_sign, 12) + 1;
}

/** Python f-string `{x:+g}` for the offset error message. */
function plus_g(x: number): string {
  return x >= 0 ? `+${x}` : `${x}`;
}

/**
 * Convert local datetime to Julian Day.
 *
 * Guard rails: swe.julday() silently accepts anything (it would happily compute a JD for
 * year 12000 or an offset of 99h), so reject inputs that are certainly data errors here
 * — dates before 1600 (this engine assumes the proleptic Gregorian calendar and modern
 * timezone data; civil birth records that old don't exist) and UTC offsets outside the
 * real-world ±14h range (a typo like 55 instead of 5.5 silently moves the chart days).
 */
export function local_to_jd(dt: PlainDateTime, timezone_offset: number): number {
  if (dt.year < 1600) {
    throw new Error(
      `Date ${formatISODate(dt)} is before 1600 — Gregorian-calendar and timezone ` +
        "assumptions do not hold that far back; check the entered date."
    );
  }
  if (!(-14.0 <= timezone_offset && timezone_offset <= 14.0)) {
    throw new Error(
      `UTC offset ${plus_g(timezone_offset)}h is outside the real-world range ±14h; ` +
        "check the timezone input."
    );
  }
  const utc_dt = addSeconds(dt, -timezone_offset * 3600);
  return swe.julday(
    utc_dt.year,
    utc_dt.month,
    utc_dt.day,
    utc_dt.hour + utc_dt.minute / 60.0 + utc_dt.second / 3600.0
  );
}

/**
 * Compute sidereal position for a single body.
 * This is the CANONICAL source for all derived properties.
 */
export function compute_sidereal_position(jd: number, body: number, _body_name: string): PlanetPosition {
  ensure_swiss_ephemeris();
  const flags = swe.FLG_SIDEREAL | swe.FLG_SPEED;
  const [res] = swe.calc_ut(jd, body, flags);

  const sidereal_longitude = norm_deg(res[0]);
  const speed = res[3];

  const [sign, sign_name, degree_in_sign] = get_sign_from_longitude(sidereal_longitude);
  const [nak_num, nak_name, pada] = get_nakshatra(sidereal_longitude);
  const is_retrograde = speed < -0.001;

  return {
    longitude: sidereal_longitude,
    sign: sign,
    sign_name: sign_name,
    degree_in_sign: degree_in_sign,
    nakshatra: nak_num,
    nakshatra_name: nak_name,
    nakshatra_pada: pada,
    speed: speed,
    is_retrograde: is_retrograde,
  };
}

/** Compute Rahu and Ketu positions based on config node type. */
export function compute_node_positions(jd: number, cfg: AstrologyConfig): [PlanetPosition, PlanetPosition] {
  ensure_swiss_ephemeris();
  const flags = swe.FLG_SIDEREAL | swe.FLG_SPEED;
  const node_body = cfg.node_type === "mean" ? swe.MEAN_NODE : swe.TRUE_NODE;
  const [res] = swe.calc_ut(jd, node_body, flags);

  const rahu_longitude = norm_deg(res[0]);
  const rahu_speed = res[3];
  const ketu_longitude = norm_deg(rahu_longitude + 180.0);

  const [rahu_sign, rahu_sign_name, rahu_deg] = get_sign_from_longitude(rahu_longitude);
  const [rahu_nak, rahu_nak_name, rahu_pada] = get_nakshatra(rahu_longitude);

  const rahu: PlanetPosition = {
    longitude: rahu_longitude,
    sign: rahu_sign,
    sign_name: rahu_sign_name,
    degree_in_sign: rahu_deg,
    nakshatra: rahu_nak,
    nakshatra_name: rahu_nak_name,
    nakshatra_pada: rahu_pada,
    speed: rahu_speed,
    is_retrograde: true,
  };

  const [ketu_sign, ketu_sign_name, ketu_deg] = get_sign_from_longitude(ketu_longitude);
  const [ketu_nak, ketu_nak_name, ketu_pada] = get_nakshatra(ketu_longitude);

  const ketu: PlanetPosition = {
    longitude: ketu_longitude,
    sign: ketu_sign,
    sign_name: ketu_sign_name,
    degree_in_sign: ketu_deg,
    nakshatra: ketu_nak,
    nakshatra_name: ketu_nak_name,
    nakshatra_pada: ketu_pada,
    speed: rahu_speed,
    is_retrograde: true,
  };

  return [rahu, ketu];
}

/** Create PlanetPosition for chart angles (Asc, MC, Desc, IC). */
export function compute_angle_position(longitude: number): PlanetPosition {
  const lon = norm_deg(longitude);
  const [sign, sign_name, degree_in_sign] = get_sign_from_longitude(lon);
  const [nak_num, nak_name, pada] = get_nakshatra(lon);

  return {
    longitude: lon,
    sign: sign,
    sign_name: sign_name,
    degree_in_sign: degree_in_sign,
    nakshatra: nak_num,
    nakshatra_name: nak_name,
    nakshatra_pada: pada,
    speed: 0.0,
    is_retrograde: false,
  };
}

/**
 * Naisargika Bala — fixed natural strength per 05_shadbala_chesht_naisargika_drik_bala.md:
 * "Sun: 60 Virupas … Moon: 51.4 … Venus: 42.8 … Jupiter: 34.3 … Mercury: 25.7 … Mars: 17.1 …
 * Saturn: 8.6 Virupas. Values derived: 60/7 × rank (7 down to 1)" — normalised ÷60 to 0..1
 * (= rank/7 exactly). The classical table has no node rows; by the "Shanivat Rahu, Kujavat
 * Ketu" convention Rahu takes Saturn's value and Ketu takes Mars's. Bodies outside the
 * navagraha (outer planets) stay neutral at 0.5.
 */
const NAISARGIKA_BALA: Record<string, number> = {
  sun: 7 / 7,
  moon: 6 / 7,
  venus: 5 / 7,
  jupiter: 4 / 7,
  mercury: 3 / 7,
  mars: 2 / 7,
  saturn: 1 / 7,
  rahu: 1 / 7, // Saturn-like
  ketu: 2 / 7, // Mars-like
};

/**
 * Compute simplified Shadbala for a planet.
 * Components: dignity + dig bala + cheshta bala + naisargika bala
 *
 * `ctx` carries the chart context the luminaries' Cheshta Bala needs (Sun longitude for the
 * Moon's Paksha Bala, ayanamsa for the Sun's Ayan Bala — see compute_cheshta_bala).
 */
export function compute_shadbala_lite(
  planet_name: string,
  pos: PlanetPosition,
  house: number,
  _rulerships: Record<number, string>,
  ctx: { sun_longitude?: number; ayanamsa?: number } = {}
): ShadbalaLite {
  // Skip angles
  if (["ascendant", "midheaven", "descendant", "imumcoeli"].includes(planet_name)) {
    return createShadbalaLite();
  }

  // 1. Dignity (Sthana bala proxy) — degree-aware: the exaltation strength (Uccha Bala)
  // peaks at the exact deep-exaltation degree and falls to ~0 at deep debilitation, with
  // own-sign/Moolatrikona flooring it. This makes the strength gate sensitive to the exact
  // longitude (it shifts second-by-second as the chart moves), not just the sign.
  const dignity_score = positional_dignity_strength(planet_name, pos.longitude, pos.sign);

  // 2. Dig bala (directional strength)
  const dig_bala = compute_dig_bala(planet_name, house);

  // 3. Cheshta bala (motional strength) — retrograde strong / fast weak per source; the
  // luminaries use Paksha Bala (Moon) and Ayan Bala (Sun) via ctx.
  const cheshta_bala = compute_cheshta_bala(planet_name, pos.speed, pos.is_retrograde, {
    longitude: pos.longitude,
    sun_longitude: ctx.sun_longitude,
    ayanamsa: ctx.ayanamsa,
  });

  // 4. Naisargika bala (fixed natural strength — see NAISARGIKA_BALA above)
  const naisargika_bala = NAISARGIKA_BALA[planet_name.toLowerCase()] ?? 0.5;

  // Weighted total (weights sum to 1.0)
  const total =
    dignity_score * 0.35 +
    dig_bala * 0.25 +
    cheshta_bala * 0.2 +
    naisargika_bala * 0.2;

  return {
    dignity_score: dignity_score,
    dig_bala: dig_bala,
    cheshta_bala: cheshta_bala,
    naisargika_bala: naisargika_bala,
    total: total,
  };
}

/** Compute bhava madhya (house centers) from whole sign cusps. */
export function compute_bhava_centers(house_cusps: number[]): number[] {
  const centers: number[] = [];
  for (let i = 0; i < 12; i++) {
    const cusp = house_cusps[i];
    let next_cusp = house_cusps[(i + 1) % 12];
    if (next_cusp < cusp) {
      next_cusp += 360;
    }
    const center = norm_deg((cusp + next_cusp) / 2.0);
    centers.push(center);
  }
  return centers;
}

/** Calculate raw chart data - pure astronomical facts. */
export function calculate_chart(
  dt: PlainDateTime,
  lat: number,
  lon: number,
  timezone_offset: number,
  cfg: AstrologyConfig
): RawChartData {
  ensure_swiss_ephemeris();
  const jd = local_to_jd(dt, timezone_offset);
  const utc_dt = addSeconds(dt, -timezone_offset * 3600);
  const ayanamsa = swe.get_ayanamsa_ut(jd);

  const planets: Record<string, PlanetPosition> = {};
  const retrograde_planets: string[] = [];

  // Calculate planet positions
  for (const [name, body] of PLANETS) {
    const pos = compute_sidereal_position(jd, body, name);
    planets[name] = pos;
    if (pos.is_retrograde) {
      retrograde_planets.push(name);
    }
  }

  // Calculate nodes
  const [rahu, ketu] = compute_node_positions(jd, cfg);
  planets["rahu"] = rahu;
  planets["ketu"] = ketu;
  retrograde_planets.push("rahu", "ketu");

  // Calculate angles. Placidus is undefined at extreme latitudes (|lat| beyond the polar
  // circles) and can raise there. The Ascendant itself is well-defined everywhere, so on
  // failure fall back to Whole-Sign house cusps (which always exist) rather than silently
  // collapsing the whole chart to 0 Aries — that would corrupt every downstream score.
  let placidus_cusps: number[] = [];
  let asc_lon: number;
  let mc_lon: number;
  try {
    const [cusps, ascmc] = swe.houses_ex(jd, lat, lon, "P", swe.FLG_SIDEREAL);
    asc_lon = norm_deg(ascmc[0]);
    mc_lon = norm_deg(ascmc[1]);
    placidus_cusps = cusps.slice(0, 12).map((c) => norm_deg(c));
  } catch {
    try {
      const [cusps, ascmc] = swe.houses_ex(jd, lat, lon, "W", swe.FLG_SIDEREAL);
      asc_lon = norm_deg(ascmc[0]);
      mc_lon = norm_deg(ascmc[1]);
      placidus_cusps = cusps.slice(0, 12).map((c) => norm_deg(c));
    } catch (exc) {
      // Both Placidus and Whole-Sign house calculation failed — this only happens at
      // extreme latitudes where the Ascendant is genuinely undefined. For a rectification
      // engine, silently scoring every candidate as 0° Aries would corrupt the entire run
      // with a confident-looking but meaningless result, so fail loudly instead.
      throw new Error(
        `Ascendant/house calculation failed for lat=${lat}, lon=${lon} ` +
          `(latitude likely too extreme for house division): ${exc}`
      );
    }
  }

  planets["ascendant"] = compute_angle_position(asc_lon);
  planets["midheaven"] = compute_angle_position(mc_lon);
  planets["descendant"] = compute_angle_position(asc_lon + 180.0);
  planets["imumcoeli"] = compute_angle_position(mc_lon + 180.0);

  // Whole Sign houses
  const asc_sign = planets["ascendant"].sign;
  const house_cusps: number[] = [];
  for (let i = 0; i < 12; i++) {
    house_cusps.push(pymod(asc_sign * 30 + i * 30, 360));
  }

  // Bhava centers
  const bhava_centers = compute_bhava_centers(house_cusps);

  // House positions for each planet
  const house_positions: Record<string, number> = {};
  for (const [name, pos] of Object.entries(planets)) {
    house_positions[name] = get_house_from_signs(pos.sign, asc_sign);
  }

  // Compute Shadbala-lite for each planet (Sun longitude + ayanamsa feed the luminaries'
  // Paksha/Ayan-Bala Cheshta — see compute_cheshta_bala)
  const shadbala_ctx = { sun_longitude: planets["sun"]?.longitude, ayanamsa: ayanamsa };
  const shadbala: Record<string, ShadbalaLite> = {};
  for (const [name, pos] of Object.entries(planets)) {
    if (!["ascendant", "midheaven", "descendant", "imumcoeli"].includes(name)) {
      const house = house_positions[name];
      shadbala[name] = compute_shadbala_lite(name, pos, house, cfg.rulerships, shadbala_ctx);
    }
  }

  // Compute functional roles
  const func_roles_data = compute_functional_roles(asc_sign, cfg.rulerships);
  const functional_roles: FunctionalRoles = {
    roles: func_roles_data["roles"],
    yogakarakas: func_roles_data["yogakarakas"],
  };

  return {
    datetime_utc: utc_dt,
    datetime_local: dt,
    julian_day: jd,
    ayanamsa: ayanamsa,
    planets: planets,
    house_cusps: house_cusps,
    ascendant_longitude: asc_lon,
    midheaven_longitude: mc_lon,
    retrograde_planets: retrograde_planets,
    house_positions: house_positions,
    bhava_centers: bhava_centers,
    placidus_cusps: placidus_cusps,
    shadbala: shadbala,
    functional_roles: functional_roles,
  };
}

/**
 * Compute BASIC classical checks for interpretability (not primary drivers).
 *
 * Returns a dict with:
 *   - combustion: per-planet combustion info vs Sun
 *   - graha_yuddha: list of detected planetary wars
 *   - moon_paksha: waxing/waning + strength proxy
 *   - dignity_flags: per-planet dignity label (from sign placement)
 */
export function compute_classical_checks(chart: RawChartData): Record<string, any> {
  const planets = chart.planets;
  if (!("sun" in planets) || !("moon" in planets)) {
    return {};
  }

  const sun_lon = planets["sun"].longitude;
  const combustion: Record<string, any> = {};
  for (const [name, pos] of Object.entries(planets)) {
    if (["sun", "moon", "ascendant", "midheaven", "descendant", "imumcoeli"].includes(name)) {
      continue;
    }
    combustion[name] = compute_combustion(sun_lon, pos.longitude, name, pos.is_retrograde);
  }

  const dignity_flags: Record<string, string> = {};
  for (const [name, pos] of Object.entries(planets)) {
    if (!["ascendant", "midheaven", "descendant", "imumcoeli"].includes(name)) {
      dignity_flags[name] = dignity_flag(name, pos.sign, pos.degree_in_sign);
    }
  }

  return {
    combustion: combustion,
    graha_yuddha: compute_graha_yuddha(planets),
    moon_paksha: compute_moon_paksha(sun_lon, planets["moon"].longitude),
    dignity_flags: dignity_flags,
  };
}
